import { ErrorResponse } from "@bitwarden/common/models/response/error.response";

/** Which step of a Cabinet file transfer failed, so support can tell them apart in the console. */
export type CabinetFileStage = "presign" | "upload" | "download";

/**
 * A failed Cabinet upload/download. Carries the i18n key to show the user plus the HTTP status
 * and gloss-api error code (when there is one) for the console, instead of a bare `false`/`null`
 * that leaves only a generic "File upload failed" toast and nothing to debug.
 */
export class CabinetFileError extends Error {
  constructor(
    readonly stage: CabinetFileStage,
    readonly messageKey: string,
    readonly status?: number,
    readonly code?: string,
    readonly originalError?: unknown,
  ) {
    super(
      `Cabinet ${stage} failed` + (status ? `: HTTP ${status}` : "") + (code ? ` ${code}` : ""),
    );
    this.name = "CabinetFileError";
  }
}

/** gloss-api vault-file error codes (vault-file.controller.js) that have a specific message. */
const API_ERROR_MESSAGE_KEYS: Record<string, string> = {
  VAULT_SIZE_LIMIT_ERROR: "cabinetStorageFull",
  FILE_SIZE_LIMIT_ERROR: "fileSizeExceedsLimit",
};

/** Normalises anything a Cabinet transfer can reject with into a CabinetFileError. */
export function toCabinetFileError(
  stage: CabinetFileStage,
  error: unknown,
  fallbackMessageKey: string,
): CabinetFileError {
  if (error instanceof CabinetFileError) {
    return error;
  }
  // DataRepositoryService.handleError resolves an authed 401/403 to null, so send() rejects
  // with null rather than an ErrorResponse. gloss-api only returns those for a failed token
  // check; a 403 from infrastructure in front of it would also land here.
  if (error == null) {
    return new CabinetFileError(stage, "loginExpired");
  }
  if (error instanceof ErrorResponse) {
    const code = error.message;
    const messageKey = API_ERROR_MESSAGE_KEYS[code] ?? fallbackMessageKey;
    return new CabinetFileError(stage, messageKey, error.statusCode, code, error);
  }
  return new CabinetFileError(stage, fallbackMessageKey, undefined, undefined, error);
}

/** Logs the failure with its stage/status/code; never logs presigned URLs, file content or keys. */
export function logCabinetFileError(error: CabinetFileError): void {
  // eslint-disable-next-line no-console
  console.error(`[Cabinet] ${error.message}`, error.originalError ?? "");
}
